import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowClockwise } from 'shared/icons';
import PageHeader from '../components/layout/PageHeader';
import Badge from '../components/ui/Badge';
import Button from '../components/ui/Button';
import ErrorCard from '../components/ui/ErrorCard';
import { SkeletonTable } from '../components/ui/Skeleton';
import StandardTable from '../components/ui/StandardTable';
import { useDebounce } from '../hooks/useDebounce';
import { useDelayedLoading } from '../hooks/useDelayedLoading';
import api from '../services/api';
import { ExtractPaging, ExtractResults } from '../utils/aggregation';
import { FormatDateTimeBr } from '../utils/date';
import { FormatMoney } from '../utils/format';
import { MLB_URLS } from '../utils/ml';

const DEFAULT_LIMIT = 15;
// Busca server-side (GET /orders?search=): espera a pessoa parar de digitar e
// só manda termos com 2+ chars — o backend trata menos que isso como ausente,
// então enviar seria um roundtrip que devolve a lista inteira de novo.
const SEARCH_DEBOUNCE_MS = 400;
const SEARCH_MIN_LENGTH = 2;

function resolveBuyer(order) {
    const buyerId = order.buyer?.id || order.buyer_id || null;
    const buyerName =
        [order.buyer?.first_name, order.buyer?.last_name]
            .map((value) => String(value || '').trim())
            .filter(Boolean)
            .join(' ') ||
        order.buyer?.nickname ||
        order.buyer_nickname ||
        '-';

    return { buyerId, buyerName };
}

function resolveOrderItems(order) {
    if (Array.isArray(order.order_items)) {
        return order.order_items;
    }

    if (!Array.isArray(order.items)) {
        return [];
    }

    return order.items.map((item) => ({
        item_id: item.item_id || item.itemId || null,
        item_title: item.item_title || item.title || null,
        quantity: item.quantity || 0,
        item_index: item.item_index || item.id || null,
    }));
}

const Orders = () => {
    const [orders, setOrders] = useState([]);
    const [paging, setPaging] = useState({ total: 0, limit: DEFAULT_LIMIT, offset: 0 });
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(DEFAULT_LIMIT);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    // Valor cru do input (controlado, pra StandardTable não desmontar/perder o
    // texto); o termo que vai pro wire é o debounced abaixo.
    const [searchTerm, setSearchTerm] = useState('');

    // Anti-flash (padrão do app): skeleton só entra se a carga passar de 300ms;
    // trocas de página rápidas não piscam.
    const showSkeleton = useDelayedLoading(loading);

    const offset = Math.max(0, (page - 1) * pageSize);

    const debouncedSearch = useDebounce(searchTerm.trim(), SEARCH_DEBOUNCE_MS);
    const effectiveSearch = debouncedSearch.length >= SEARCH_MIN_LENGTH ? debouncedSearch : '';

    // Termo efetivo mudou → volta pra página 1 (a página N do termo antigo não
    // existe no resultado novo). Ref do valor anterior evita disparar no mount
    // (e no mount duplicado do StrictMode), que resetaria a página sem motivo.
    const previousSearchRef = useRef(effectiveSearch);
    useEffect(() => {
        if (previousSearchRef.current === effectiveSearch) {
            return;
        }
        previousSearchRef.current = effectiveSearch;
        setPage(1);
    }, [effectiveSearch]);

    // `signal` é opcional: o effect passa o AbortController da rodada (e aborta
    // no cleanup); o botão "Atualizar" e o retry do ErrorCard chamam sem nada.
    const loadOrders = useCallback(
        async (signal) => {
            try {
                setLoading(true);
                setError(null);

                // Paginação e busca no SERVIDOR — wire snake_case (CLAUDE.md §2):
                // limit/offset/search. `search` só entra quando há termo efetivo;
                // string vazia no wire não é contrato.
                // A API v2 responde envelope { results, paging: { total, limit, offset[, truncated] } }.
                const query = new URLSearchParams({
                    limit: String(pageSize),
                    offset: String(offset),
                    include_items: 'true',
                });
                if (effectiveSearch) {
                    query.set('search', effectiveSearch);
                }
                const data = await api.get(`/orders?${query.toString()}`, { cache: 'no-store', signal });
                const sourceOrders = ExtractResults(data, ['results', 'orders']);
                const normalizedOrders = sourceOrders.map((order) => {
                    const normalizedItems = resolveOrderItems(order);
                    const { buyerId, buyerName } = resolveBuyer(order);
                    const totalAmount = Number(order.total_amount || order?.totals?.order_total_amount || 0);

                    return {
                        ...order,
                        ml_order_id: order.ml_order_id || order.order_id || order.external_order_id || order.id,
                        order_items: normalizedItems,
                        buyer_id: buyerId,
                        buyer_name: buyerName,
                        total_amount: Number.isFinite(totalAmount) ? totalAmount : 0,
                    };
                });

                setOrders(normalizedOrders);
                setPaging(ExtractPaging(data, { total: normalizedOrders.length, limit: pageSize, offset }));
            } catch (err) {
                // Abortada de propósito (troca de página/termo, unmount): a resposta
                // que importa é a da request nova — nem erro nem estado.
                if (err?.name === 'AbortError') {
                    return;
                }

                const errorMessage = err.message || err.response?.data?.message || 'Erro desconhecido';

                if (errorMessage.includes('Token de acesso expirado') || errorMessage.includes('token_expired')) {
                    setError({
                        title: 'Token expirado',
                        message:
                            'O token de acesso do Mercado Livre expirou (validade de 6 horas). Entre em contato com o administrador para renovar o token usando o refresh token.',
                        action: 'Ver instrucoes',
                        actionLink: '/health',
                        type: 'warning',
                    });
                } else if (errorMessage.includes('Sem permissao') || errorMessage.includes('403')) {
                    setError({
                        title: 'Sem permissao',
                        message:
                            'A aplicacao nao tem permissao para acessar pedidos. Verifique os escopos necessarios (read, write) na aplicacao do Mercado Livre.',
                        action: null,
                        type: 'error',
                    });
                } else if (errorMessage.includes('nao configurado') || errorMessage.includes('nao encontrado')) {
                    setError({
                        title: 'Configuracao pendente',
                        message: `${errorMessage}\n\nConfigure o arquivo ml_config_essentials.py com as credenciais do Mercado Livre.`,
                        action: 'Ver instrucoes',
                        actionLink: '/health',
                        type: 'info',
                    });
                } else if (errorMessage.includes('USER_ID')) {
                    setError({
                        title: 'USER_ID incorreto',
                        message: 'O USER_ID configurado esta incorreto ou nao foi encontrado.',
                        action: 'Tentar novamente',
                        actionFn: loadOrders,
                        type: 'error',
                    });
                } else {
                    setError({
                        title: 'Erro ao carregar pedidos',
                        message: errorMessage,
                        action: 'Tentar novamente',
                        actionFn: loadOrders,
                        type: 'error',
                    });
                }
            } finally {
                // Se ESTA rodada foi abortada, a nova já assumiu o loading — baixar
                // aqui apagaria o spinner da request que ainda está em voo.
                if (!signal?.aborted) {
                    setLoading(false);
                }
            }
        },
        [pageSize, offset, effectiveSearch],
    );

    // Uma request em voo por vez: o cleanup aborta a anterior quando página,
    // tamanho ou termo mudam (e no unmount). Sem isso o StrictMode duplicava o
    // GET no mount em dev, e paginar rápido deixava a resposta ANTIGA chegar
    // depois e sobrescrever a página nova.
    useEffect(() => {
        const controller = new AbortController();
        loadOrders(controller.signal);
        return () => controller.abort();
    }, [loadOrders]);

    const columns = useMemo(
        () => [
            {
                key: 'ml_order_id',
                label: 'Pedido',
                getValue: (order) => order.ml_order_id || order.id,
                render: (order) => (
                    <span className="font-medium text-black dark:text-white">{order.ml_order_id || order.id}</span>
                ),
            },
            {
                key: 'date_created',
                label: 'Data',
                getValue: (order) => order.date_created,
                render: (order) => FormatDateTimeBr(order.date_created, { blank: '-' }),
            },
            {
                key: 'products',
                label: 'Produtos',
                getValue: (order) => order.order_items.map((item) => item.item_title || item.item_id).join(' | '),
                render: (order) =>
                    order.order_items.length > 0 ? (
                        <div className="space-y-1">
                            {order.order_items.map((item) => {
                                const itemId = item.item_id;
                                const itemTitle = item.item_title || 'Produto';
                                const productUrl = itemId ? MLB_URLS.listing(itemId) : null;

                                return (
                                    <div key={`${order.id}-${item.item_index || item.item_id}`} className="flex gap-2">
                                        {productUrl ? (
                                            <a
                                                href={productUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="line-clamp-2 text-blue-600 hover:underline dark:text-blue-400"
                                                title={`Ver anuncio: ${itemTitle}`}
                                            >
                                                {itemTitle}
                                            </a>
                                        ) : (
                                            <span className="line-clamp-2">{itemTitle}</span>
                                        )}
                                        <span className="whitespace-nowrap text-xs text-mono-500">
                                            x{item.quantity}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        '-'
                    ),
            },
            {
                key: 'status',
                label: 'Status',
                getValue: (order) => order.status,
                render: (order) => <Badge variant="secondary">{order.status || '-'}</Badge>,
            },
            {
                key: 'buyer',
                label: 'Comprador',
                getValue: (order) => `${order.buyer_name || '-'} ${order.buyer_id || ''}`.trim(),
                render: (order) => {
                    const buyerId = order.buyer_id;
                    const buyerName = order.buyer_name || '-';
                    if (!buyerId) {
                        return buyerName;
                    }

                    return (
                        <Link to={`/customers/${buyerId}`} className="text-blue-600 hover:underline dark:text-blue-400">
                            {buyerName}
                        </Link>
                    );
                },
            },
            {
                key: 'total_amount',
                label: 'Valor',
                getValue: (order) => order.total_amount || 0,
                render: (order) => (
                    <span className="font-medium text-black dark:text-white">
                        {FormatMoney(order.total_amount, { blank: '-' })}
                    </span>
                ),
            },
        ],
        [],
    );

    if (error) {
        return (
            <div className="space-y-6">
                <PageHeader title="Pedidos" subtitle="Ultimos pedidos sincronizados com o Mercado Livre" />
                <ErrorCard
                    title={error.title}
                    message={error.message}
                    action={error.action}
                    actionFn={error.actionFn}
                    actionLink={error.actionLink}
                    type={error.type}
                />
            </div>
        );
    }

    // Só a carga INICIAL (sem dados e sem termo digitado) troca a tabela pelo
    // skeleton: até os 300ms do useDelayedLoading vencerem não renderiza nada
    // (evita flash do empty state antes do skeleton/dados). Com termo no input
    // a tabela fica montada mesmo vazia — senão o campo de busca desmonta e o
    // foco cai enquanto a pessoa digita.
    const isInitialLoading = loading && orders.length === 0 && !searchTerm;

    return (
        <div className="space-y-6">
            <PageHeader
                title="Pedidos"
                subtitle="Ultimos pedidos sincronizados com o Mercado Livre"
                actions={
                    // Sem passar `loadOrders` direto: onClick entregaria o
                    // MouseEvent como `signal`.
                    <Button onClick={() => loadOrders()} size="sm">
                        <ArrowClockwise size={18} className="mr-2" />
                        Atualizar
                    </Button>
                }
            />

            {isInitialLoading ? (
                showSkeleton ? (
                    <SkeletonTable columns={columns.length} rows={Math.min(pageSize, DEFAULT_LIMIT)} />
                ) : null
            ) : (
                // Stale-while-loading: em recargas (página/busca) a tabela FICA
                // montada — trocar pelo skeleton desmontava o input de busca e
                // derrubava o foco no meio da digitação. Enquanto a carga passa
                // dos 300ms a tabela só esmaece e bloqueia cliques.
                <div
                    aria-busy={loading}
                    className={showSkeleton ? 'opacity-60 pointer-events-none transition-opacity' : undefined}
                >
                    {paging.truncated ? (
                        <p className="mb-2 text-xs text-mono-500 dark:text-mono-400">
                            A busca bateu no limite de {paging.total.toLocaleString('pt-BR')} pedidos mais recentes —
                            refine o termo para ver todos.
                        </p>
                    ) : null}
                    <StandardTable
                        rows={orders}
                        columns={columns}
                        rowKey={(row) => row.id}
                        countLabel="pedidos"
                        exportEnabled
                        exportFileName="pedidos"
                        emptyMessage="Nenhum pedido encontrado."
                        // Busca no SERVIDOR (GET /orders?search=) sobre TODOS os
                        // pedidos visíveis ao tenant — pedido, comprador ou
                        // produto; status NÃO entra (tem o filtro `status`
                        // próprio). O input é controlado aqui pra o termo
                        // sobreviver às recargas e passar pelo debounce.
                        searchMode="server"
                        searchTerm={searchTerm}
                        onSearchTermChange={setSearchTerm}
                        searchPlaceholder="Pesquisar em todos os pedidos: número do pedido, comprador ou produto"
                        paginationMode="server"
                        totalItems={paging.total}
                        currentPage={page}
                        onPageChange={setPage}
                        itemsPerPage={pageSize}
                        onItemsPerPageChange={(nextPageSize) => {
                            setPageSize(nextPageSize);
                            setPage(1);
                        }}
                        pageSizeOptions={[15, 30, 50, 100]}
                    />
                </div>
            )}
        </div>
    );
};

export default Orders;
